/**
 * BossAI.ts - Boss phase system and special attack patterns
 *
 * This is a component/helper class used by Enemy when the enemy is a boss.
 * It does NOT subclass Enemy - instead Enemy calls bossAI.update() during combat.
 */

import { Vector2 } from '@shared/types';
import { Entity } from './Entity';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface BossPhase {
  /** HP percentage threshold at which this phase activates (e.g. 0.6 = 60% HP) */
  hpThreshold: number;
  /** Available attack patterns in this phase */
  attackPatterns: BossAttackPattern[];
  /** Speed multiplier applied while in this phase */
  speedMultiplier: number;
  /** Damage multiplier applied while in this phase */
  damageMultiplier: number;
  /** Optional callback invoked once when the phase begins */
  onEnter?: (enemy: Entity) => void;
}

export interface BossAttackPattern {
  /** Unique identifier for this pattern */
  id: string;
  /** Cooldown in seconds between uses */
  cooldown: number;
  /** Current remaining cooldown (seconds) */
  currentCooldown: number;
  /** Maximum range at which this attack can be used */
  range: number;
  /** Wind-up time before damage is applied (seconds). 0 = instant. */
  castTime?: number;
  /** Execute the attack and return a result describing what happened */
  execute: (enemy: Entity, target: Entity, deltaTime: number) => BossAttackResult;
}

export interface BossAttackResult {
  /** Direct damage dealt to the primary target */
  damage: number;
  /** If set, the attack is an AOE centered at aoePosition with this radius */
  aoeRadius?: number;
  /** Center of the AOE in world coordinates */
  aoePosition?: Vector2;
  /** Knockback force applied to targets hit */
  knockbackForce?: number;
  /** Minions to spawn as part of this attack */
  spawnMinions?: MinionSpawn[];
  /** Status effect key to apply to targets hit */
  statusEffect?: string;
  /** The attack pattern id that produced this result */
  attackId?: string;
}

/** Active telegraph: a pending boss attack with visible ground indicator */
export interface BossTelegraph {
  /** The attack pattern id */
  attackId: string;
  /** World position where the indicator is shown */
  position: Vector2;
  /** Radius of the indicator (0 = no ground indicator) */
  radius: number;
  /** Total wind-up duration */
  duration: number;
  /** Elapsed time since telegraph started */
  elapsed: number;
  /** The boss entity performing the attack */
  bossRef: Entity;
  /** The target entity */
  targetRef: Entity;
  /** The attack pattern to execute when telegraph completes */
  pattern: BossAttackPattern;
  /** Visual color hint (element-based) */
  color: string;
}

export interface MinionSpawn {
  /** Enemy type identifier (matches the class names used by the spawner) */
  typeId: string;
  /** Number of minions to spawn */
  count: number;
  /** Spawn radius offset from the boss */
  offsetRadius: number;
}

// ---------------------------------------------------------------------------
// Boss phase definitions
// ---------------------------------------------------------------------------

function getAlbastiKralicesiPhases(): BossPhase[] {
  return [
    // Phase 1: 100-60% HP - Basic melee + Poison Pool attacks
    {
      hpThreshold: 1.0,
      attackPatterns: [
        {
          id: 'melee_slash',
          cooldown: 1.0,
          currentCooldown: 0,
          range: 60,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return { damage: 40 };
          },
        },
        {
          id: 'melee_combo',
          cooldown: 3.5,
          currentCooldown: 1.5,
          range: 70,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            // Two-hit combo: claw swipe followed by tail lash
            return { damage: 25 + 20, knockbackForce: 80 };
          },
        },
        {
          id: 'poison_pool',
          cooldown: 5.0,
          currentCooldown: 2.0,
          range: 200,
          castTime: 0.8,  // Telegraph: green circle appears 0.8s before damage
          execute: (_enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 10,
              aoeRadius: 90,
              aoePosition: { x: target.position.x, y: target.position.y },
              statusEffect: 'poison',
            };
          },
        },
        {
          id: 'poison_spit',
          cooldown: 4.0,
          currentCooldown: 3.0,
          range: 250,
          execute: (_enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Ranged poison projectile that leaves a smaller lingering pool
            return {
              damage: 18,
              aoeRadius: 45,
              aoePosition: { x: target.position.x, y: target.position.y },
              statusEffect: 'poison',
            };
          },
        },
      ],
      speedMultiplier: 1.0,
      damageMultiplier: 1.0,
    },
    // Phase 2: 60-30% HP - Faster, spirit bolt barrage + teleport
    {
      hpThreshold: 0.6,
      attackPatterns: [
        {
          id: 'melee_slash',
          cooldown: 0.8,
          currentCooldown: 0,
          range: 60,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return { damage: 40 };
          },
        },
        {
          id: 'poison_pool',
          cooldown: 4.5,
          currentCooldown: 1.0,
          range: 200,
          castTime: 0.7,  // Slightly faster telegraph in phase 2
          execute: (_enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Larger pool in phase 2
            return {
              damage: 15,
              aoeRadius: 110,
              aoePosition: { x: target.position.x, y: target.position.y },
              statusEffect: 'poison',
            };
          },
        },
        {
          id: 'spirit_bolt',
          cooldown: 2.5,
          currentCooldown: 0,
          range: 280,
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            const dx = target.position.x - enemy.position.x;
            const dy = target.position.y - enemy.position.y;
            const distSq = dx * dx + dy * dy;
            const inRange = distSq <= 280 * 280;
            return {
              damage: inRange ? 30 : 0,
              statusEffect: 'kut_burn',
            };
          },
        },
        {
          id: 'shadow_barrage',
          cooldown: 6.0,
          currentCooldown: 3.0,
          range: 250,
          castTime: 0.6,  // Telegraph: purple indicator 0.6s before barrage
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Rapid multi-hit attack: 5 spirit bolts in a fan pattern
            // Each bolt does moderate damage; combined they are devastating if
            // the player does not dodge
            const dx = target.position.x - enemy.position.x;
            const dy = target.position.y - enemy.position.y;
            const distSq = dx * dx + dy * dy;
            const inRange = distSq <= 250 * 250;
            // Total burst: 5 x 15 = 75 raw damage (before multiplier)
            return {
              damage: inRange ? 75 : 0,
              aoeRadius: 60,
              aoePosition: { x: target.position.x, y: target.position.y },
              statusEffect: 'kut_burn',
            };
          },
        },
        {
          id: 'teleport',
          cooldown: 8.0,
          currentCooldown: 4.0,
          range: 9999,
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Teleport to a flanking position behind or beside the player
            const angle = Math.atan2(
              target.position.y - enemy.position.y,
              target.position.x - enemy.position.x,
            );
            // Appear behind the player (offset by PI) with slight random jitter
            const behindAngle = angle + Math.PI + (Math.random() - 0.5) * 0.8;
            const dist = 70 + Math.random() * 50; // 70-120 units behind target
            enemy.position.x = target.position.x + Math.cos(behindAngle) * dist;
            enemy.position.y = target.position.y + Math.sin(behindAngle) * dist;
            return { damage: 0 };
          },
        },
      ],
      speedMultiplier: 1.2,
      damageMultiplier: 1.3,
      onEnter: (_enemy: Entity): void => {
        // Phase 2 transition: boss lets out a shriek, handled visually by caller
      },
    },
    // Phase 3: 30-0% HP - Enraged: shadow clones, ground slam, all previous attacks
    {
      hpThreshold: 0.3,
      attackPatterns: [
        {
          id: 'melee_slash',
          cooldown: 0.55,
          currentCooldown: 0,
          range: 65,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            // Frenzied slashing - slightly wider reach
            return { damage: 45 };
          },
        },
        {
          id: 'poison_pool',
          cooldown: 3.5,
          currentCooldown: 0.5,
          range: 220,
          castTime: 0.5,  // Even faster in phase 3 - pressure the player
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Dual poison pools: one at target, visual cue for second at boss
            return {
              damage: 20,
              aoeRadius: 120,
              aoePosition: {
                x: (target.position.x + enemy.position.x) / 2,
                y: (target.position.y + enemy.position.y) / 2,
              },
              statusEffect: 'poison',
            };
          },
        },
        {
          id: 'spirit_bolt',
          cooldown: 1.8,
          currentCooldown: 0,
          range: 300,
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            const dx = target.position.x - enemy.position.x;
            const dy = target.position.y - enemy.position.y;
            const distSq = dx * dx + dy * dy;
            const inRange = distSq <= 300 * 300;
            return {
              damage: inRange ? 35 : 0,
              statusEffect: 'kut_burn',
            };
          },
        },
        {
          id: 'shadow_barrage',
          cooldown: 4.5,
          currentCooldown: 2.0,
          range: 270,
          castTime: 0.4,  // Fast telegraph in phase 3 - harder to dodge
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Upgraded barrage: 7 bolts in a wider spread, higher damage
            const dx = target.position.x - enemy.position.x;
            const dy = target.position.y - enemy.position.y;
            const distSq = dx * dx + dy * dy;
            const inRange = distSq <= 270 * 270;
            // Total burst: 7 x 15 = 105 raw damage (before multiplier)
            return {
              damage: inRange ? 105 : 0,
              aoeRadius: 75,
              aoePosition: { x: target.position.x, y: target.position.y },
              statusEffect: 'kut_burn',
            };
          },
        },
        {
          id: 'teleport',
          cooldown: 6.0,
          currentCooldown: 2.0,
          range: 9999,
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Faster teleport with a damage burst on arrival
            const angle = Math.atan2(
              target.position.y - enemy.position.y,
              target.position.x - enemy.position.x,
            );
            const behindAngle = angle + Math.PI + (Math.random() - 0.5) * 1.0;
            const dist = 60 + Math.random() * 40;
            enemy.position.x = target.position.x + Math.cos(behindAngle) * dist;
            enemy.position.y = target.position.y + Math.sin(behindAngle) * dist;
            // Shockwave on arrival
            return {
              damage: 25,
              aoeRadius: 80,
              aoePosition: { x: enemy.position.x, y: enemy.position.y },
              knockbackForce: 120,
            };
          },
        },
        {
          id: 'ground_slam',
          cooldown: 7.0,
          currentCooldown: 3.0,
          range: 130,
          castTime: 1.0,  // Long telegraph - gives player time to dodge this devastating attack
          execute: (enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            // Devastating ground slam - large AOE with stun and knockback
            return {
              damage: 80,
              aoeRadius: 160,
              aoePosition: { x: enemy.position.x, y: enemy.position.y },
              knockbackForce: 250,
              statusEffect: 'stun',
            };
          },
        },
        {
          id: 'shadow_clones',
          cooldown: 20.0,
          currentCooldown: 5.0,
          range: 9999,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            // Summon shadow clones that mirror the boss's attacks briefly
            return {
              damage: 0,
              spawnMinions: [{ typeId: 'albasti_shadow', count: 2, offsetRadius: 120 }],
            };
          },
        },
        {
          id: 'summon_adds',
          cooldown: 18.0,
          currentCooldown: 8.0,
          range: 9999,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            // Summon 2-3 lesser albasti minions to overwhelm the player
            const count = 2 + Math.floor(Math.random() * 2); // 2 or 3
            return {
              damage: 0,
              spawnMinions: [{ typeId: 'albasti', count, offsetRadius: 150 }],
            };
          },
        },
      ],
      speedMultiplier: 1.4,
      damageMultiplier: 1.6,
      onEnter: (_enemy: Entity): void => {
        // Enrage transition: screen shake + visual distortion handled by caller
      },
    },
  ];
}

function getKarabasanMiniBossPhases(): BossPhase[] {
  // Single phase with special mechanics implemented via attack patterns
  return [
    {
      hpThreshold: 1.0,
      attackPatterns: [
        {
          id: 'dark_aura',
          cooldown: 0.5,
          currentCooldown: 0,
          range: 100,
          execute: (_enemy: Entity, _target: Entity, deltaTime: number): BossAttackResult => {
            // 2 DPS within 100 range - applied per tick
            return { damage: 2 * deltaTime };
          },
        },
        {
          id: 'teleport',
          cooldown: 5.0,
          currentCooldown: 3.0,
          range: 9999,
          execute: (enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            // Teleport to random position within 200 units of player
            const angle = Math.random() * Math.PI * 2;
            const dist = 80 + Math.random() * 120; // 80-200 units away
            enemy.position.x = target.position.x + Math.cos(angle) * dist;
            enemy.position.y = target.position.y + Math.sin(angle) * dist;
            return { damage: 0 };
          },
        },
        {
          id: 'shadow_clones',
          cooldown: 999.0, // One-time trigger at 50% HP (handled by special logic)
          currentCooldown: 0,
          range: 9999,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 0,
              spawnMinions: [{ typeId: 'karabasan_clone', count: 2, offsetRadius: 80 }],
            };
          },
        },
        {
          id: 'nightmare_bolt',
          cooldown: 2.0,
          currentCooldown: 0,
          range: 200,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 25,
              statusEffect: 'fear',
            };
          },
        },
      ],
      speedMultiplier: 1.0,
      damageMultiplier: 1.0,
    },
  ];
}

function getDagDeviPhases(): BossPhase[] {
  return [
    // Phase 1: 100-25% HP - Ground slam, rock throw, normal melee
    {
      hpThreshold: 1.0,
      attackPatterns: [
        {
          id: 'melee',
          cooldown: 1.2,
          currentCooldown: 0,
          range: 70,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return { damage: 55 };
          },
        },
        {
          id: 'ground_slam',
          cooldown: 6.0,
          currentCooldown: 2.0,
          range: 120,
          execute: (enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 70,
              aoeRadius: 120,
              aoePosition: { x: enemy.position.x, y: enemy.position.y },
              knockbackForce: 150,
              statusEffect: 'stun',
            };
          },
        },
        {
          id: 'rock_throw',
          cooldown: 4.0,
          currentCooldown: 1.0,
          range: 300,
          execute: (_enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 80,
              aoeRadius: 60,
              aoePosition: { x: target.position.x, y: target.position.y },
            };
          },
        },
      ],
      speedMultiplier: 1.0,
      damageMultiplier: 1.0,
    },
    // Phase 2: 25-0% HP - Enrage, faster slams
    {
      hpThreshold: 0.25,
      attackPatterns: [
        {
          id: 'melee',
          cooldown: 0.8,
          currentCooldown: 0,
          range: 70,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return { damage: 55 };
          },
        },
        {
          id: 'ground_slam',
          cooldown: 3.5,
          currentCooldown: 0,
          range: 120,
          execute: (enemy: Entity, _target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 80,
              aoeRadius: 120,
              aoePosition: { x: enemy.position.x, y: enemy.position.y },
              knockbackForce: 200,
              statusEffect: 'stun',
            };
          },
        },
        {
          id: 'rock_throw',
          cooldown: 3.0,
          currentCooldown: 0,
          range: 300,
          execute: (_enemy: Entity, target: Entity, _dt: number): BossAttackResult => {
            return {
              damage: 90,
              aoeRadius: 70,
              aoePosition: { x: target.position.x, y: target.position.y },
            };
          },
        },
      ],
      speedMultiplier: 1.4,
      damageMultiplier: 1.3,
      onEnter: (_enemy: Entity): void => {
        // Enrage transition
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Phase resolver
// ---------------------------------------------------------------------------

function getBossPhases(bossType: string): BossPhase[] {
  switch (bossType) {
    case 'albasti_kralicesi':
      return getAlbastiKralicesiPhases();
    case 'karabasan_miniboss':
      return getKarabasanMiniBossPhases();
    case 'dag_devi':
      return getDagDeviPhases();
    default:
      // Fallback: single phase with a basic melee pattern
      return [{
        hpThreshold: 1.0,
        attackPatterns: [{
          id: 'melee',
          cooldown: 1.0,
          currentCooldown: 0,
          range: 60,
          execute: (_enemy: Entity, _target: Entity, _dt: number): BossAttackResult => ({ damage: 30 }),
        }],
        speedMultiplier: 1.0,
        damageMultiplier: 1.0,
      }];
  }
}

// ---------------------------------------------------------------------------
// BossAI class
// ---------------------------------------------------------------------------

export class BossAI {
  private phases: BossPhase[];
  private currentPhaseIndex: number = 0;
  private specialCooldowns: Map<string, number> = new Map();
  private bossType: string;

  /** Tracks whether the Karabasan shadow clones have been spawned (one-time at 50% HP) */
  private shadowClonesSpawned: boolean = false;

  /** Active telegraphs waiting to resolve */
  public activeTelegraphs: BossTelegraph[] = [];

  /** Whether a phase transition just happened this frame */
  public phaseTransitionThisFrame: boolean = false;

  /** The phase index that was just entered (valid when phaseTransitionThisFrame is true) */
  public newPhaseIndex: number = 0;

  constructor(bossType: string) {
    this.bossType = bossType;
    this.phases = getBossPhases(bossType);
  }

  /**
   * Main update tick. Call from Enemy.update() when the enemy is a boss.
   * Returns a BossAttackResult if an attack was executed this frame, or null.
   */
  update(enemy: Entity, target: Entity, deltaTime: number): BossAttackResult | null {
    const hpPercent = enemy.getHealthPercent();
    this.phaseTransitionThisFrame = false;

    // --- Check phase transitions ---
    const newPhaseIndex = this.resolvePhaseIndex(hpPercent);
    if (newPhaseIndex !== this.currentPhaseIndex) {
      this.currentPhaseIndex = newPhaseIndex;
      this.newPhaseIndex = newPhaseIndex;
      this.phaseTransitionThisFrame = true;
      const phase = this.phases[this.currentPhaseIndex];
      if (phase.onEnter) {
        phase.onEnter(enemy);
      }
    }

    const currentPhase = this.phases[this.currentPhaseIndex];

    // --- Update all pattern cooldowns ---
    for (const pattern of currentPhase.attackPatterns) {
      if (pattern.currentCooldown > 0) {
        pattern.currentCooldown -= deltaTime;
        if (pattern.currentCooldown < 0) {
          pattern.currentCooldown = 0;
        }
      }
    }

    // Update special cooldowns
    for (const [key, value] of this.specialCooldowns) {
      const updated = value - deltaTime;
      if (updated <= 0) {
        this.specialCooldowns.delete(key);
      } else {
        this.specialCooldowns.set(key, updated);
      }
    }

    // --- Update active telegraphs ---
    const resolvedResult = this.updateTelegraphs(enemy, target, deltaTime, currentPhase);
    if (resolvedResult) {
      return resolvedResult;
    }

    // Don't select new attacks while a telegraph is active
    if (this.activeTelegraphs.length > 0) {
      return null;
    }

    // --- Karabasan special: one-time shadow clones at 50% HP ---
    if (this.bossType === 'karabasan_miniboss' && !this.shadowClonesSpawned && hpPercent <= 0.5) {
      this.shadowClonesSpawned = true;
      const clonePattern = currentPhase.attackPatterns.find(p => p.id === 'shadow_clones');
      if (clonePattern) {
        const result = clonePattern.execute(enemy, target, deltaTime);
        result.attackId = clonePattern.id;
        return result;
      }
    }

    // --- Select and execute best available attack ---
    const dx = target.position.x - enemy.position.x;
    const dy = target.position.y - enemy.position.y;
    const distSq = dx * dx + dy * dy;

    let bestPattern: BossAttackPattern | null = null;
    let bestPriority = -1;

    for (let i = 0; i < currentPhase.attackPatterns.length; i++) {
      const pattern = currentPhase.attackPatterns[i];

      // Skip if on cooldown
      if (pattern.currentCooldown > 0) continue;

      // Skip if target out of range (squared distance comparison)
      if (distSq > pattern.range * pattern.range) continue;

      // Skip one-time patterns (shadow clones)
      if (pattern.id === 'shadow_clones') continue;

      // Higher-index patterns have higher priority (special attacks come after basic ones)
      const priority = i;
      if (priority > bestPriority) {
        bestPriority = priority;
        bestPattern = pattern;
      }
    }

    if (bestPattern) {
      // If the attack has a cast time, start a telegraph instead of executing immediately
      const castTime = bestPattern.castTime || 0;
      if (castTime > 0) {
        this.startTelegraph(enemy, target, bestPattern, castTime);
        bestPattern.currentCooldown = bestPattern.cooldown;
        return null; // No immediate result; will resolve when telegraph completes
      }

      // Execute the chosen pattern immediately
      const result = bestPattern.execute(enemy, target, deltaTime);
      result.attackId = bestPattern.id;

      // Apply phase damage multiplier
      result.damage *= currentPhase.damageMultiplier;

      // Reset cooldown
      bestPattern.currentCooldown = bestPattern.cooldown;

      return result;
    }

    return null;
  }

  /**
   * Start a telegraph (ground indicator) for a delayed attack.
   */
  private startTelegraph(enemy: Entity, target: Entity, pattern: BossAttackPattern, duration: number): void {
    // Determine telegraph position and radius based on attack type
    let pos: Vector2;
    let radius: number;
    let color: string;

    if (pattern.id === 'ground_slam') {
      pos = { x: enemy.position.x, y: enemy.position.y };
      radius = 160;
      color = '#ff4444';
    } else if (pattern.id === 'poison_pool') {
      pos = { x: target.position.x, y: target.position.y };
      radius = 110;
      color = '#44ff44';
    } else if (pattern.id === 'shadow_barrage') {
      pos = { x: target.position.x, y: target.position.y };
      radius = 75;
      color = '#aa44ff';
    } else {
      // Default: target position
      pos = { x: target.position.x, y: target.position.y };
      radius = 80;
      color = '#ffaa44';
    }

    this.activeTelegraphs.push({
      attackId: pattern.id,
      position: { x: pos.x, y: pos.y },
      radius,
      duration,
      elapsed: 0,
      bossRef: enemy,
      targetRef: target,
      pattern,
      color,
    });
  }

  /**
   * Tick active telegraphs. Returns a BossAttackResult if a telegraph resolved.
   */
  private updateTelegraphs(enemy: Entity, target: Entity, deltaTime: number, currentPhase: BossPhase): BossAttackResult | null {
    let resolvedResult: BossAttackResult | null = null;

    for (let i = this.activeTelegraphs.length - 1; i >= 0; i--) {
      const tele = this.activeTelegraphs[i];
      tele.elapsed += deltaTime;

      if (tele.elapsed >= tele.duration) {
        // Telegraph resolved - execute the attack
        const result = tele.pattern.execute(enemy, target, deltaTime);
        result.attackId = tele.pattern.id;
        result.damage *= currentPhase.damageMultiplier;
        resolvedResult = result;
        this.activeTelegraphs.splice(i, 1);
      }
    }

    return resolvedResult;
  }

  /**
   * Get the current phase object based on HP percentage.
   */
  getCurrentPhase(hpPercent: number): BossPhase {
    return this.phases[this.resolvePhaseIndex(hpPercent)];
  }

  /**
   * Get the speed multiplier for the current phase.
   */
  getSpeedMultiplier(hpPercent: number): number {
    return this.getCurrentPhase(hpPercent).speedMultiplier;
  }

  /**
   * Get the damage multiplier for the current phase.
   */
  getDamageMultiplier(hpPercent: number): number {
    return this.getCurrentPhase(hpPercent).damageMultiplier;
  }

  /**
   * Resolve which phase index is active based on HP percentage.
   * Phases are sorted by threshold descending. The last phase whose
   * threshold is >= current HP% is the active one.
   */
  private resolvePhaseIndex(hpPercent: number): number {
    let index = 0;
    for (let i = 1; i < this.phases.length; i++) {
      if (hpPercent <= this.phases[i].hpThreshold) {
        index = i;
      }
    }
    return index;
  }
}
